import { TFunction, useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { Checkbox, Form, Input, Modal } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import FormItem from '../../components/form/FormItem';
import api from '../../api';
import OmniNotification from 'components/OmniNotification';
import styles from './UpdateProjectNameModal.module.less';
import cx from 'classnames';
const getValidationSchema = (t: TFunction) =>
	yup.object().shape({
		name: yup
			.string()
			.trim()
			.required(
				t('validation.required', {
					fieldName: t('ProjectSetupPage.Name'),
				}),
			)
			.max(
				50,
				t('validation.maxLength', {
					fieldName: t('ProjectSetupPage.Name'),
					count: 50,
				}),
			),
	});

interface Props {
	visible: boolean;
	setVisible: (visible: boolean) => void;
	afterSubmit: (name?: string) => void;
	projectId: number;
	projectName?: string;
}

interface FormValues {
	applyToStore: boolean | undefined;
	name: string;
}

export default function UpdateProjectNameModal(props: Props) {
	const { projectId, visible, setVisible, projectName } = props;
	const { t } = useTranslation();
	const [modalLoading, setModalLoading] = useState(false);
	const validationSchema = useMemo(() => getValidationSchema(t), [t]);
	const { control, handleSubmit, formState, reset, setValue } =
		useForm<FormValues>({
			mode: 'all',
			resolver: yupResolver(validationSchema),
			defaultValues: {
				name: projectName,
				applyToStore: false,
			},
		});

	useEffect(() => setValue('name', projectName || ''), [projectName]);

	const submit = async (values: FormValues) => {
		const name = values.name.trim();
		setModalLoading(true);
		try {
			await api.updateProjectName(projectId, {
				name: name,
				applyToStore: values.applyToStore,
			});

			OmniNotification.success({
				message: t('projectSummaryPage.UpdateProjectName.Success.Header'),
				description: t('projectSummaryPage.UpdateProjectName.Success.Body', {
					name: name,
				}),
			});
			props.afterSubmit(name);
		} catch (err) {
			OmniNotification.error({
				message: t('notification.error.title'),
				description: t('api.defaultError'),
			});
			props.afterSubmit();
		}

		setModalLoading(false);
	};

	return (
		<>
			{visible && (
				<Modal
					title={<span>{t('projectSummaryPage.UpdateProjectName')}</span>}
					visible
					centered
					okText={t('common.SaveChanges')}
					cancelText={t('common.cancel')}
					onOk={handleSubmit(submit)}
					onCancel={() => {
						setVisible(false);
						reset();
					}}
					cancelButtonProps={{
						className: 'color-dark',
					}}
					okButtonProps={
						{
							type: 'primary',
							htmlType: 'submit',
							disabled: !formState.isValid,
							'data-testid': 'update-project-name-modal-submit-button',
						} as any
					}
					confirmLoading={modalLoading}
					destroyOnClose
					maskClosable={false}
					keyboard={false}
				>
					<>
						<Form data-testid="form" layout="vertical">
							<FormItem
								name="name"
								control={control}
								label={t('ProjectSetupPage.Name')}
								className={cx('has-counter', styles.textField)}
								render={({ field }) => (
									<>
									<Input {...field} maxLength={50} data-testid="name-input" />
									<span className="form-counter">
										{field.value?.length || 0}/50
									</span>
									</>
								)}
							/>
							<FormItem
								className={styles.updateProjectNameModal}
								name="applyToStore"
								control={control}
								render={({ field: { onChange, value } }) => {
									return (
										<Checkbox checked={value} onChange={onChange}>
											<span className={styles.checkboxLabel}>
												{t(
													'projectSummaryPage.UpdateProjectName.Checkbox.Label',
												)}
											</span>
										</Checkbox>
									);
								}}
							/>
						</Form>
					</>
				</Modal>
			)}
		</>
	);
}
